import { type MouseEvent, useState } from 'react';
import {
  CardActionArea,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Skeleton,
  Typography,
} from '@mui/material';
import Avatar from '@mui/material/Avatar';
import CardContent from '@mui/material/CardContent';
import DownloadIcon from '@mui/icons-material/Download';
import { ErrorBoundary } from 'react-error-boundary';
import IosShareIcon from '@mui/icons-material/IosShare';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { CardPrimaryIconButton, StyledCard } from './ExposeAppCard.styles';
import { useAppStatusProps, useShouldHideCost } from './ExposeAppCard.hooks';

import { BenefitsStatusIcon } from '../../../BenefitsStatusIcon';
import { CardFooterCollapsible } from '../../CardFooterCollapsible';
import { CardFooterOverlay } from '../../CardFooterOverlay';
import { CardHeader } from '../../CardHeader';
import { CardMenu } from '../../CardMenu';
import { ContainedText } from '../../../ContainedText';
import type { AppCardProps } from '../../AppCardTypes.types';
import { CardCategories, CardScreenshots } from '../Shared';
import { AppPlatformInfo } from '../../AppPlatformInfo';
import { AppPlatformImageInfo } from '../../AppPlatformImageInfo';

/**
 * Props for configuring an expose app card component from [MicroLibraryCoreUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibraryCoreUI)
 *
 * @property {string} appImages - The images to display in the card.
 * @property {string} avatarImage - The image to display in the card header.
 * @property {CardHeaderProps} CardHeaderProps - The props for the card header.
 */
export const ExposeAppCard = ({
  appImages,
  avatarImage,
  CardHeaderProps,
  CardProps,
  categories,
  cost,
  isAppFavourited,
  isDefaultOpen,
  isProUser,
  menuOptions,
  onClick,
  platforms,
  recommendButton,
  downloadButton,
  title,
  translations,
  appStatus,
  isLoading,
  appPlatformType,
  platformConfiguration,
  FlexibleImageComponent,
}: AppCardProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);

  const appStatusProps = useAppStatusProps(appStatus, translations);

  const handleMenuButtonClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    handleMenuClose();
  };

  const handleRecommendButtonClick = (
    event: MouseEvent<HTMLElement>,
    callback: () => void
  ) => {
    event.stopPropagation();
    callback();
  };

  const handleDownloadButtonClick = (
    event: MouseEvent<HTMLElement>,
    callback: () => void
  ) => {
    event.stopPropagation();
    callback();
  };

  const [isCardOpen, setIsCardOpen] = useState(isDefaultOpen ?? false);
  const handleOpenClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setIsCardOpen(!isCardOpen);
  };

  const shouldHideCost = useShouldHideCost(appStatus);

  return (
    <>
      <StyledCard
        sx={{
          ...(onClick && {
            cursor: 'pointer',
          }),
        }}
        raised
        onClick={onClick}
        data-testid="app-card"
        {...CardProps}
      >
        <CardActionArea component="div" sx={{ height: '360px' }}>
          <CardHeader
            title={
              isLoading ? (
                <Skeleton width="200" data-testid="app-card-title-loading">
                  <Typography>.</Typography>
                </Skeleton>
              ) : (
                title
              )
            }
            avatar={
              isLoading ? (
                <Skeleton
                  variant="rounded"
                  data-testid="app-card-logo-loading"
                  height={40}
                  width={40}
                />
              ) : (
                <Avatar variant="rounded" data-testid="app-card-logo">
                  {avatarImage}
                </Avatar>
              )
            }
            action={
              isLoading ? null : (
                <>
                  {recommendButton && (
                    <CardPrimaryIconButton
                      onMouseDown={(event) => event.stopPropagation()}
                      onTouchStart={(event) => event.stopPropagation()}
                      onClick={(event) =>
                        handleRecommendButtonClick(
                          event,
                          recommendButton.onClick
                        )
                      }
                      title={recommendButton.label}
                      data-testid="app-card-recommend-button"
                    >
                      <IosShareIcon />
                    </CardPrimaryIconButton>
                  )}

                  {downloadButton && (
                    <CardPrimaryIconButton
                      onMouseDown={(event) => event.stopPropagation()}
                      onTouchStart={(event) => event.stopPropagation()}
                      onClick={(event) =>
                        handleDownloadButtonClick(event, downloadButton.onClick)
                      }
                      title={downloadButton.label}
                      data-testid="app-card-download-button"
                    >
                      <DownloadIcon />
                    </CardPrimaryIconButton>
                  )}

                  {menuOptions && (
                    <IconButton
                      onClick={handleMenuButtonClick}
                      aria-controls={isMenuOpen ? 'card-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={isMenuOpen ? 'true' : undefined}
                      title={menuOptions?.openTitle}
                      onMouseDown={(event) => event.stopPropagation()}
                      onTouchStart={(event) => event.stopPropagation()}
                      data-testid="app-card-menu-button"
                    >
                      <MoreVertIcon color="primary" />
                    </IconButton>
                  )}
                </>
              )
            }
            data-testid="app-card-header"
            {...CardHeaderProps}
          />
          <CardContent sx={{ paddingBottom: '24px' }}>
            <CardCategories categories={categories} isLoading={isLoading} />
          </CardContent>
          <Divider light />
          <Collapse
            orientation="vertical"
            in={isCardOpen}
            data-testid="app-card-content-collapsible"
          >
            <CardContent>
              <Grid container spacing={2}>
                {appStatusProps && !isLoading && (
                  <Grid item data-testid="app-card-app-status">
                    <BenefitsStatusIcon
                      status={appStatusProps.status}
                      label={appStatusProps.costType}
                    />
                  </Grid>
                )}

                {cost && !shouldHideCost && !isLoading && (
                  <Grid item data-testid="app-card-cost">
                    <ContainedText>{cost}</ContainedText>
                  </Grid>
                )}

                {isLoading && (
                  <>
                    <Grid item>
                      <Skeleton
                        variant="rounded"
                        data-testid="app-card-cost-loading"
                      >
                        <ContainedText>Absolutely free</ContainedText>
                      </Skeleton>
                    </Grid>
                    <Grid item xs={12} container spacing={2}>
                      <Grid item xs={4}>
                        <Skeleton
                          variant="rounded"
                          height={68}
                          data-testid="app-card-platform-loading"
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <Skeleton
                          variant="rounded"
                          height={68}
                          data-testid="app-card-platform-loading"
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <Skeleton
                          variant="rounded"
                          height={68}
                          data-testid="app-card-platform-loading"
                        />
                      </Grid>
                    </Grid>
                  </>
                )}
                {platforms &&
                  (appPlatformType === 'Threshold' ? (
                    <AppPlatformImageInfo
                      platforms={platforms}
                      isLoading={isLoading}
                      FlexibleImageComponent={FlexibleImageComponent}
                      thresholds={platformConfiguration?.thresholds}
                    />
                  ) : (
                    <AppPlatformInfo
                      platforms={platforms}
                      isLoading={isLoading}
                    />
                  ))}
              </Grid>
            </CardContent>
          </Collapse>
          {isCardOpen && <Divider light />}
          <CardFooterCollapsible
            handleCollapseClick={handleOpenClick}
            isCollapsed={isCardOpen}
            status={appStatusProps?.status}
          >
            <CardContent sx={{ paddingBottom: '8px !important' }}>
              <CardScreenshots
                appImages={appImages as any}
                isLoading={isLoading as any}
              />
            </CardContent>
          </CardFooterCollapsible>
        </CardActionArea>
        {appStatusProps && <CardFooterOverlay {...appStatusProps} />}
      </StyledCard>

      {menuOptions && (
        <CardMenu
          anchorEl={anchorEl}
          id="card-menu"
          open={isMenuOpen}
          onClose={handleMenuClose}
          onClick={handleMenuClick}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          isAppFavourited={isAppFavourited}
          isProUser={isProUser}
          {...menuOptions}
        />
      )}
    </>
  );
};

const ErrorFallback = () => {
  return <ExposeAppCard />;
};

export const AppCardWithErrorBoundary = (props: AppCardProps) => {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <ExposeAppCard {...props} />
    </ErrorBoundary>
  );
};
